// Extract product links and images from HTML content
export function extractProductData(html) {
  const products = {};
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const anchors = doc.querySelectorAll("a");
  const images = doc.querySelectorAll("img");

  // Collect all Amazon product images
  const allImageUrls = [];
  images.forEach((img) => {
    const src = img.src;
    if (
      src?.includes("media-amazon.com/images/I/") ||
      src?.includes("images-amazon.com/images/I/")
    ) {
      const cleanSrc = src.replace(/\._[^.]+_\./, "._SL500_.");
      allImageUrls.push(cleanSrc);
    }
  });

  let productIndex = 0;
  anchors.forEach((a) => {
    const href = a.href;
    const text = a.textContent?.trim();

    if (href && text && text.length > 20) {
      const amazonMatch = href.match(
        /amazon\.com.*?\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i,
      );
      if (amazonMatch) {
        const asin = amazonMatch[1];
        const imageUrl = allImageUrls[productIndex] || null;
        products[text] = { url: href, asin, imageUrl };
        productIndex++;
      }
    }
  });

  return products;
}

// Parse receipt text and extract product information
export function parseReceipt(text, productLinks = {}) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const isAmazon = text.includes("Order #") || text.includes("Amazon") || text.includes("Sold by:");

  if (isAmazon) {
    return parseAmazonReceipt(text, lines, productLinks);
  }
  return parseGenericReceipt(lines);
}

function parseAmazonReceipt(text, lines, productLinks = {}) {
  const products = [];

  let orderSubtotal = null;
  let orderGrandTotal = null;
  let orderDate = null;

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];

    if (!orderDate && /order\s*placed/i.test(line)) {
      const dateMatch = line.match(
        /(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/i,
      );
      if (dateMatch) {
        orderDate = new Date(dateMatch[0]);
      } else if (k + 1 < lines.length) {
        const nextDateMatch = lines[k + 1].match(
          /(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/i,
        );
        if (nextDateMatch) {
          orderDate = new Date(nextDateMatch[0]);
        }
      }
    }
    if (line.includes("Item(s) Subtotal")) {
      const priceMatch = line.match(/\$(\d+\.?\d*)/);
      if (priceMatch) {
        orderSubtotal = parseFloat(priceMatch[1]);
      } else if (k + 1 < lines.length) {
        const nextMatch = lines[k + 1].match(/^\$(\d+\.?\d*)/);
        if (nextMatch) orderSubtotal = parseFloat(nextMatch[1]);
      }
    }
    if (line.includes("Grand Total")) {
      const totalMatch = line.match(/\$(\d+\.?\d*)/);
      if (totalMatch) {
        orderGrandTotal = parseFloat(totalMatch[1]);
      } else if (k + 1 < lines.length) {
        const nextMatch = lines[k + 1].match(/^\$(\d+\.?\d*)/);
        if (nextMatch) orderGrandTotal = parseFloat(nextMatch[1]);
      }
    }
  }

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (isSkippableLine(line)) {
      i++;
      continue;
    }

    if (line.length > 30 && !line.includes(":") && !line.startsWith("$")) {
      let j = i + 1;
      let foundConfirmation = false;
      let retailPrice = null;
      let purchasePrice = null;
      let discount = null;
      let quantity = 1;

      while (j < lines.length && j < i + 15) {
        const nextLine = lines[j];
        if (nextLine.startsWith("Sold by:") || nextLine.includes("Return items:")) {
          foundConfirmation = true;
        }

        const qtyMatch = nextLine.match(/^(?:Qty|Quantity)[:\s]*(\d+)/i) || nextLine.match(/^x(\d+)$/i);
        if (qtyMatch) {
          quantity = parseInt(qtyMatch[1], 10);
        }

        const discountMatch = nextLine.match(/^-\$(\d+\.?\d*)/);
        if (discountMatch) {
          discount = parseFloat(discountMatch[1]);
          j++;
          continue;
        }

        const priceMatch = nextLine.match(/^\$(\d+\.?\d*)/);
        if (priceMatch && !nextLine.includes("Subtotal") && !nextLine.includes("Total")) {
          retailPrice = parseFloat(priceMatch[1]);
          foundConfirmation = true;
          break;
        }
        j++;
      }

      if (retailPrice !== null) {
        if (discount !== null) {
          purchasePrice = retailPrice - discount;
        } else if (orderGrandTotal !== null && orderSubtotal !== null && orderSubtotal > 0) {
          const itemShare = retailPrice / orderSubtotal;
          purchasePrice = orderGrandTotal * itemShare;
        } else {
          purchasePrice = retailPrice;
        }
      }

      if (foundConfirmation) {
        let name = line;
        if (i + 1 < lines.length && lines[i + 1] === line) {
          i++;
        }

        let matchedData = productLinks[name];
        if (!matchedData) {
          for (const [key, value] of Object.entries(productLinks)) {
            const nameStart = name.substring(0, 40).toLowerCase().trim();
            const keyStart = key.substring(0, 40).toLowerCase().trim();
            if (nameStart === keyStart || name.includes(key) || key.includes(name)) {
              matchedData = value;
              break;
            }
          }
        }

        products.push({
          name,
          purchasePrice,
          retailPrice,
          source: "Amazon",
          quantity,
          url: matchedData?.url || null,
          asin: matchedData?.asin || null,
          imageUrl: matchedData?.imageUrl || null,
          purchaseDate: orderDate,
        });
      }
    }
    i++;
  }

  return products;
}

function parseGenericReceipt(lines) {
  const products = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isSkippableLine(line)) continue;

    const priceMatch = line.match(/\$(\d+\.?\d*)/);
    if (priceMatch) {
      const price = parseFloat(priceMatch[1]);
      let name = line.replace(/\$\d+\.?\d*/, "").trim();
      name = name.replace(/\s*(x\d+|qty:?\s*\d+)$/i, "").trim();

      if (name.length > 5 && name.length < 200) {
        products.push({
          name,
          purchasePrice: price,
          retailPrice: price,
          source: "Receipt",
          quantity: 1,
        });
      }
    }
  }

  return products;
}

function isSkippableLine(line) {
  const skipPatterns = [
    /^order\s*(#|details|placed|summary)/i,
    /^(ship|shipping|delivered|payment|total|subtotal|tax|grand)/i,
    /^(view|track|return|share|ask|leave|write|buy|get)\s/i,
    /^your\s(account|orders|package|item)/i,
    /^item\(?s?\)?\s*subtotal/i,
    /^promotion\s*applied/i,
    /^estimated\s*tax/i,
    /^eligible\s*through/i,
    /^\$0\.00$/,
    /^-\$\d/,
    /^\d{3}-\d+-\d+/,
    /^[A-Z]{2}\s+\d{5}/,
    /^United States$/i,
  ];

  return skipPatterns.some((pattern) => pattern.test(line));
}
